import React, { useContext, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { ProjectService } from "@app/projects";
import { ProviderSetService } from "@app/provider-sets";
import { Breadcrumbs, BreadcrumbTemplateEnum, Page, PageHeader, PageInner, PageSection } from "@app/shared";
import { TileProps } from "@app/shared/interfaces/dashboard";
import { RoleRestricted, Roles, UserContext } from "@app/user";
import { BenchmarkingService } from "@app/overviews/network-benchmarking-dashboard";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QesIcon } from "@app/shared/icons/QESIcon";

export const InternalReports: React.FC<React.PropsWithChildren<unknown>> = () => {
  const [exportingProjectConfigs, setExportingProjectConfigs] = useState(false);
  const [exportingProviderNetworkReport, setExportingProviderNetworkReport] = useState(false);
  const [exportingCountyStatusReport, setExportingCountyStatusReport] = useState(false);
  const [exportingBenchmarkingReport, setExportingBenchmarkingReport] = useState(false);

  const projectService = new ProjectService();
  const providerSetService = new ProviderSetService();
  const benchmarkingService = new BenchmarkingService();

  const userContext = useContext(UserContext);

  const handleExportProjectConfigs = (): void => {
    if (!exportingProjectConfigs) {
      setExportingProjectConfigs(true);

      projectService.getProjectDetailsCsv(
        `Project Configurations(${(userContext.roles.includes(Roles.QUEST_ADMIN) || (userContext.roles.includes(Roles.QUEST_INTERNAL_REPORTS) && userContext.clientName === "Quest Analytics")) ? "All" : userContext.clientName})_${moment.utc().local().format("yyyyMMDD_hhmmss")}`,
        (!userContext.roles.includes(Roles.QUEST_ADMIN) && (!userContext.roles.includes(Roles.QUEST_INTERNAL_REPORTS) || userContext.clientName !== "Quest Analytics")) ? userContext.clientId : null
      ).then(() => {
        setExportingProjectConfigs(false);
      });
    }
  }

  const handleExportProviderNetworkConfigs = (): void => {
    if (!exportingProviderNetworkReport) {
      setExportingProviderNetworkReport(true);

      providerSetService.getProviderSetNetworkTaxonomyReport(
        `Provider Network Configuration(${(userContext.roles.includes(Roles.QUEST_ADMIN) || (userContext.roles.includes(Roles.QUEST_INTERNAL_REPORTS) && userContext.clientName === "Quest Analytics")) ? "All" : userContext.clientName})_${moment.utc().local().format("yyyyMMDD_hhmmss")}`,
        (!userContext.roles.includes(Roles.QUEST_ADMIN) && (!userContext.roles.includes(Roles.QUEST_INTERNAL_REPORTS) || userContext.clientName !== "Quest Analytics")) ? userContext.clientId : null
      ).then(() => {
        setExportingProviderNetworkReport(false);
      });
    }
  }

  const handleExportCountyStatuses = (): void => {
    if (!exportingCountyStatusReport) {
      setExportingCountyStatusReport(true);

      projectService.getProjectCountyStatusesCsv(
        `Project County Statuses(${(userContext.roles.includes(Roles.QUEST_ADMIN) || (userContext.roles.includes(Roles.QUEST_INTERNAL_REPORTS) && userContext.clientName === "Quest Analytics")) ? "All" : userContext.clientName})_${moment.utc().local().format("yyyyMMDD_hhmmss")}`,
        (!userContext.roles.includes(Roles.QUEST_ADMIN) && (!userContext.roles.includes(Roles.QUEST_INTERNAL_REPORTS) || userContext.clientName !== "Quest Analytics")) ? userContext.clientId : null
      ).then(() => {
        setExportingCountyStatusReport(false);
      });
    }
  }

  const handleExportBenchmarkingCoverage = (): void => {
    if (!exportingBenchmarkingReport) {
      setExportingBenchmarkingReport(true);
      benchmarkingService.getDataCoverageCsv(
        `Competitive Insights Data Coverage(${(userContext.roles.includes(Roles.QUEST_ADMIN) || userContext.roles.includes(Roles.QUEST_INTERNAL_REPORTS) && userContext.clientName === "Quest Analytics") ? "All" : userContext.clientName})_${moment.utc().local().format("yyyyMMDD_hhmmss")}`,
        (!userContext.roles.includes(Roles.QUEST_ADMIN) && (!userContext.roles.includes(Roles.QUEST_INTERNAL_REPORTS) || userContext.clientName !== "Quest Analytics")) ? userContext.clientId : null
      ).then(() => {
        setExportingBenchmarkingReport(false);
      });
    }
  }

  const tiles: TileProps[] = [
    {
      allowedRoles: [Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN],
      icon: QesIcons.FontAwesome.List,
      route: "role-assignments",
      title: "Role Assignments",
    },
    {
      allowedRoles: [Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN, Roles.QUEST_INTERNAL_REPORTS],
      classNames: exportingProjectConfigs ? "disabled" : null,
      icon: exportingProjectConfigs ? QesIcons.FontAwesome.Spinner : QesIcons.FontAwesome.Download,
      onClick: handleExportProjectConfigs,
      title: `Export ${(userContext.roles.includes(Roles.QUEST_ADMIN) || (userContext.roles.includes(Roles.QUEST_INTERNAL_REPORTS) && userContext.clientName === "Quest Analytics")) ? "Project Configuration (All Clients)" : "Client Project Configuration"}`,
    },
    {
      allowedRoles: [Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN, Roles.QUEST_INTERNAL_REPORTS],
      classNames: `text-center new-line ${exportingProviderNetworkReport ? "disabled" : null}`,
      icon: exportingProviderNetworkReport ? QesIcons.FontAwesome.Spinner : QesIcons.FontAwesome.Download,
      onClick: handleExportProviderNetworkConfigs,
      title: `Export ${(userContext.roles.includes(Roles.QUEST_ADMIN) || (userContext.roles.includes(Roles.QUEST_INTERNAL_REPORTS) && userContext.clientName === "Quest Analytics")) ? "Provider Network Configuration \n (All Clients)" : "Client Provider Network Configuration"}`,
    },
    {
      allowedRoles: [Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN, Roles.QUEST_INTERNAL_REPORTS],
      classNames: `text-center new-line ${exportingCountyStatusReport ? "disabled" : null}`,
      icon: exportingCountyStatusReport ? QesIcons.FontAwesome.Spinner : QesIcons.FontAwesome.Download,
      onClick: handleExportCountyStatuses,
      title: `Export ${(userContext.roles.includes(Roles.QUEST_ADMIN) || (userContext.roles.includes(Roles.QUEST_INTERNAL_REPORTS) && userContext.clientName === "Quest Analytics")) ? "Project County Statuses (All Clients)" : "Client Project County Statuses"}`,
    },
    {
      allowedRoles: [Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN, Roles.QUEST_INTERNAL_REPORTS],
      classNames: `text-center new-line ${exportingBenchmarkingReport ? "disabled" : null}`,
      icon: exportingBenchmarkingReport ? QesIcons.FontAwesome.Spinner : QesIcons.FontAwesome.Download,
      onClick: handleExportBenchmarkingCoverage,
      title: `Export Competitive Insights Data Coverage`,
    },
  ];

  return (
    <Page documentTitle="Internal Reports - Admin">
      <Breadcrumbs template={BreadcrumbTemplateEnum.InternalReports} items={[]} />
      <PageInner>
        <PageHeader iconName={QesIcons.FontAwesome.FilesOutline} title="Internal Reports" disableHR={false} />
        <PageSection>
          <div className="container mt-3">
            {tiles.map((tile) => <Tile key={tile.title} classNames={tile.classNames} icon={tile.icon} route={tile.route} title={tile.title} allowedRoles={tile.allowedRoles} onClick={tile.onClick} />)}
          </div>
        </PageSection>
      </PageInner>
    </Page>
  );
};

const Tile: React.FC<React.PropsWithChildren<TileProps>> = ({ classNames, icon, route = "#", onClick, title, allowedRoles }) => {
  return (
    <RoleRestricted allowedRoles={allowedRoles}>
      <Link to={`/admin/internal-reports/${route}`} className={`dashboard-tile col-4 ${classNames}`} onClick={!!onClick ? (() => onClick()) : undefined}>
        <QesIcon iconName={icon} width="26" height="26" classNames={icon === QesIcons.FontAwesome.Spinner ? "qes-icon-spin" : ""} isDecorative={true} />
        <h6>{title}</h6>
      </Link>
    </RoleRestricted>
  );
};
